import { useMemo, useState, useEffect, useCallback } from 'react';
import { useUser } from 'structure/user/UserContext';
import { useSDAOLaunchpadClaim } from './useSDAOLaunchpadClaim';
import { userClaimableList } from 'utils/constants/claim.const';
import { NetworkChainIds } from '@dapp/multi-lib/network';

type ClaimableItem = (typeof userClaimableList)[0];

type ItemState = {
  isLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  claimData: any;
  isButtonEnabled: boolean;
};

// Hook that filters claimable list and returns only items where button is enabled
export function useFilteredClaimableList() {
  const { chainId } = useUser();
  const [itemStates, setItemStates] = useState<Map<number, ItemState>>(
    new Map()
  );

  // Update state for a specific item
  const updateItemState = useCallback((index: number, state: ItemState) => {
    setItemStates((prev) => {
      const next = new Map(prev);
      next.set(index, state);
      return next;
    });
  }, []);

  // Process all items
  const processedItems = useMemo(() => {
    if (!userClaimableList) return [];

    return userClaimableList.map((item, index) => {
      const state = itemStates.get(index);
      return {
        item,
        index,
        state: state || {
          isLoading: true,
          claimData: undefined,
          isButtonEnabled: false,
        },
      };
    });
  }, [itemStates]);

  // Filter items - only show if there's something claimable on the target network
  const filteredItems = useMemo(() => {
    return processedItems
      .filter(({ item, state }) => {
        const checkSelectedPoolNetwork = item?.poolChainId !== chainId;

        // If still loading, don't show it yet (wait for data to determine if claimable)
        if (state.isLoading) {
          return false; // Don't show while loading - wait to determine if claimable
        }

        // If no claimData after loading is complete, treat as no claimable (error or no account)
        if (!state.claimData) {
          return false; // No data means nothing to claim
        }

        // Check if there's anything claimable on the target network
        const isClaimableAmountZero =
          Number(state.claimData?.claimableAmount) === 0;
        const hasClaimableOnTargetNetwork = !isClaimableAmountZero;

        if (checkSelectedPoolNetwork) {
          // On wrong network - only show if there's something to claim on target network
          return hasClaimableOnTargetNetwork;
        }
        // On correct network - only show if button is enabled (has claimable amount)
        return state.isButtonEnabled;
      })
      .map(({ item }) => item);
  }, [processedItems, chainId]);

  // Check if all items have loaded (either successfully or with error)
  // An item is considered "loaded" if it's not loading anymore
  const allItemsLoaded = useMemo(() => {
    if (processedItems.length === 0) return true;
    return processedItems.every(({ state }) => !state.isLoading);
  }, [processedItems]);

  return {
    filteredItems,
    allItemsLoaded,
    isLoading: !allItemsLoaded,
    processedItems,
    updateItemState,
  };
}

// Hook to check individual item state
export function useClaimableItemState(
  item: ClaimableItem,
  index: number,
  onStateChange: (index: number, state: ItemState) => void
) {
  const { chainId } = useUser();
  // Always read from the target chain (pool's chain) to check claimable amount
  // This allows us to check claimable tokens without requiring network switch
  const { data: claimData, loading } = useSDAOLaunchpadClaim(
    item?.projectClaimContractAddress,
    item.poolId,
    item.poolChainId as NetworkChainIds
  );

  const checkSelectedPoolNetwork = item?.poolChainId !== chainId;
  const isClaimableAmountZero = Number(claimData?.claimableAmount) === 0;

  // Check if there's anything claimable on the target network (pool's chain)
  // This is what matters - if nothing is claimable on the target network, hide the item
  const hasClaimableOnTargetNetwork = useMemo(() => {
    if (!claimData) return false; // Still loading or no data
    // Always check the claimable amount regardless of current network
    // If claimable amount is 0, there's nothing to claim on the target network
    return !isClaimableAmountZero;
  }, [claimData, isClaimableAmountZero]);

  // Button is enabled when:
  // - On wrong network (user can switch) AND there's something claimable on target network OR
  // - On correct network AND has claimable amount > 0 AND claimData exists
  const isButtonEnabled = useMemo(() => {
    if (!claimData) return false;
    if (checkSelectedPoolNetwork) {
      // On wrong network - button enabled only if there's something to claim on target network
      return hasClaimableOnTargetNetwork;
    }
    // On correct network - button enabled if there's claimable amount
    return !isClaimableAmountZero;
  }, [
    claimData,
    checkSelectedPoolNetwork,
    isClaimableAmountZero,
    hasClaimableOnTargetNetwork,
  ]);

  // Report state changes
  useEffect(() => {
    onStateChange(index, {
      isLoading: loading,
      claimData,
      isButtonEnabled,
    });
  }, [index, loading, claimData, isButtonEnabled, onStateChange]);

  return {
    claimData,
    isLoading: loading,
    isButtonEnabled,
    hasClaimableOnTargetNetwork,
  };
}
